import * as Sentry from "@sentry/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";

import { faCircleCheck, faTriangleExclamation } from "@performyard/fontawesome/pro-solid-svg-icons";
import { ASYNC_TASK_NAMES } from "@performyard/models";
import { ButtonStandard, PageBanner, Text, toast } from "@performyard/py-components";
import {
	type AsyncTaskStatusUpdateEventType,
	FIELD_HISTORY_IMPORT_ABANDONED_AFTER_MS,
	FIELD_HISTORY_IMPORT_FILE_TOO_LARGE_MESSAGE,
	FIELD_HISTORY_IMPORT_GENERIC_FAILURE_MESSAGE,
	type FieldHistoryImportError,
	ImportFieldHistoryMetadataSchema,
	MAX_FIELD_HISTORY_IMPORT_FILE_BYTES,
	MAX_FIELD_HISTORY_IMPORT_FILE_MB,
	MAX_FIELD_HISTORY_IMPORT_ROWS_LABEL,
	type ValidateFieldHistoryImportResponse,
} from "@performyard/service-contracts";

import { AdminContainer, AdminTitle } from "../../../components/Admin";
import PageTitle from "../../../components/PageTitle";
import Spacer from "../../../components/Spacer";
import ExpectedFormatPanel from "./components/ExpectedFormatPanel";
import ImportProgress from "./components/ImportProgress";
import ValidationResults from "./components/ValidationResults";

import { useCompanySettings, useImportFieldHistory, useValidateFieldHistoryImport } from "../../../api/hooks";
import { useAsyncTaskStream } from "../../../hooks/useAsyncTaskStream";

import { getActiveCompanyTasksByName } from "../../../api/services/defs/internal-api/async-tasks";

import pluralizer from "../../../utils/pluralizer";

import ErrorPage from "../../Error";

// `failed` is separate from `invalid`: only an invalid file can be fixed and re-chosen.
type Status = "idle" | "validating" | "invalid" | "valid" | "importing" | "failed";

const SUBTITLE = `Add historical values for any employee data field. Entries are added to field history only and never change current values. Files are limited to ${MAX_FIELD_HISTORY_IMPORT_ROWS_LABEL} rows and ${MAX_FIELD_HISTORY_IMPORT_FILE_MB}MB.`;

const STREAM_LOST_MESSAGE =
	"We lost contact with the import before it reported finishing. It may still be running, so give it a few minutes before uploading again — a second import is refused while one is in progress.";

// Per company: an overlord can switch companies without the tab's storage being cleared.
const taskStorageKey = (companyId: string): string => `field-history-import-task:${companyId}`;

/** A task listed as active past the point where no run can be alive was stranded, not running. */
const isStillRunnable = (candidate: AsyncTaskStatusUpdateEventType): boolean => {
	const startedAt = candidate.startedAt ?? candidate.createdAt;
	if (!startedAt) return true;

	return Date.now() - new Date(startedAt).getTime() < FIELD_HISTORY_IMPORT_ABANDONED_AFTER_MS;
};

/** The task is the only place the result exists: the request that started it returns nothing but an id. */
const readTaskOutcome = (
	metadata: unknown
): { fileName: string; errors: FieldHistoryImportError[]; totalErrorCount: number } | { systemFailure: string } => {
	const parsed = ImportFieldHistoryMetadataSchema.safeParse(metadata);

	// With no error list there is no table to render, so this falls through to the cautious message.
	if (parsed.success && parsed.data.failureKind === "validation" && parsed.data.errors?.length) {
		return {
			// The task carries it so a restored import can still name the file in its results.
			fileName: parsed.data.fileName,
			errors: parsed.data.errors,
			totalErrorCount: parsed.data.totalErrorCount ?? parsed.data.errors.length,
		};
	}

	// Unreadable metadata is treated as a system failure: the write may have run, so the cautious
	// message is the correct one.
	const userMessage = parsed.success ? parsed.data.userMessage : undefined;
	return { systemFailure: userMessage ?? FIELD_HISTORY_IMPORT_GENERIC_FAILURE_MESSAGE };
};

const ImportFieldHistory = () => {
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [file, setFile] = useState<File | null>(null);
	// Outlives `file`, which the paths that clear the selection drop while their results stay on screen.
	const [fileName, setFileName] = useState<string | null>(null);
	const [status, setStatus] = useState<Status>("idle");
	const [result, setResult] = useState<ValidateFieldHistoryImportResponse | null>(null);
	const [taskId, setTaskId] = useState<string | null>(null);
	const [failure, setFailure] = useState<{ title: string; message: string } | null>(null);

	const queryCache = useQueryClient();
	const validateMutation = useValidateFieldHistoryImport();
	const importMutation = useImportFieldHistory();
	// Bounded: the default budget resets on every connect, so a connect-then-drop loop would retry
	// forever and never report the lost contact the effect below waits for.
	const {
		task,
		error: streamError,
		isConnected,
		isRetrying,
		hasConnected,
	} = useAsyncTaskStream(taskId, {
		boundRetriesOnStalledProgress: true,
	});

	const { data: companySettings, isPending: isCompanySettingsPending } = useCompanySettings();
	const companyId = companySettings?.company?.id ?? null;

	// Company-wide, not per-user: an import blocks the company, so whoever opens the page next needs it.
	const { data: activeTasks, isPending: isActiveTasksPending } = useQuery({
		// Keyed by company: a cached list from another one would hand the restore an unreadable task.
		queryKey: ["active-field-history-import-tasks", companyId],
		queryFn: () => getActiveCompanyTasksByName(ASYNC_TASK_NAMES.IMPORT_FIELD_HISTORY),
		meta: { disableDefaultErrorHandling: true },
	});

	/** Every task-id write goes through here so the stored copy can't outlive the state it stands for. */
	const rememberTask = (id: string | null) => {
		setTaskId(id);
		if (!companyId) return;
		if (id) {
			sessionStorage.setItem(taskStorageKey(companyId), id);
		} else {
			sessionStorage.removeItem(taskStorageKey(companyId));
		}
	};

	const reset = () => {
		setFile(null);
		setFileName(null);
		setStatus("idle");
		setResult(null);
		rememberTask(null);
		setFailure(null);
	};

	/** A rejected file: nothing was written, so the results table's copy applies. */
	const showFileError = (message: string) => {
		setFile(null);
		rememberTask(null);
		setFailure(null);
		setResult({ success: false, entryCount: 0, employeeCount: 0, errors: [{ message }], totalErrorCount: 1 });
		setStatus("invalid");
	};

	/**
	 * Anything that isn't a rejected file: the import may have partially landed.
	 *
	 * @param retainTaskId - the task hasn't settled, so keep the stored id for the next visit to read
	 *   the real outcome off. A settled task clears it, or its failure re-reports on every visit.
	 */
	const showFailure = (message: string, { retainTaskId = false }: { retainTaskId?: boolean } = {}) => {
		if (!retainTaskId) rememberTask(null);
		setResult(null);
		// Partial writes may have landed, so the profile's history is no longer what it was cached as.
		queryCache.invalidateQueries({ queryKey: ["employeeCustomFieldsHistory"] });
		setFailure({ title: "Import didn't finish", message });
		setStatus("failed");
	};

	/** The file is untouched and still uploadable, so it keeps its result and Upload re-arms. */
	const showRetryableFailure = (message: string) => {
		rememberTask(null);
		setFailure({ title: "Import not started", message });
		setStatus(result?.success ? "valid" : "idle");
	};

	// Keyed by task so a later import in the same session can still report its own strand.
	const strandedTaskId = useRef<string | null>(null);

	// Three terminal states, not two: the server re-validates, so a file that passed the interactive
	// check can still be rejected if a value, an employee or a field changed in between.
	useEffect(() => {
		if (!taskId || !task || task._id !== taskId) return;

		if (task.status === "completed") {
			toast.success("Import successful");
			// History only — `employeeCustomFields` holds current values, which an import never changes.
			queryCache.invalidateQueries({ queryKey: ["employeeCustomFieldsHistory"] });
			reset();
			return;
		}

		// The task's own age, so this holds however the id was restored — the active-task lookup it was
		// screened against can have failed, and a stored id is never screened at all when it has settled.
		// Reported once, and the id is kept: this compares a server timestamp against the browser's
		// clock, so a skewed clock must not bury an outcome still to arrive. Whatever the stream
		// delivers next wins, and the next visit's restore drops the id if nothing ever does.
		if (task.status === "processing" && !isStillRunnable(task)) {
			if (strandedTaskId.current === task._id) return;
			strandedTaskId.current = task._id;
			setFile(null);
			showFailure(FIELD_HISTORY_IMPORT_GENERIC_FAILURE_MESSAGE, { retainTaskId: true });
			return;
		}

		if (task.status === "failed") {
			const outcome = readTaskOutcome(task.metadata);
			if ("systemFailure" in outcome) {
				setFile(null);
				showFailure(outcome.systemFailure);
				return;
			}
			rememberTask(null);
			setFile(null);
			setFileName(outcome.fileName);
			setFailure(null);
			setResult({
				success: false,
				entryCount: 0,
				employeeCount: 0,
				errors: outcome.errors,
				totalErrorCount: outcome.totalErrorCount,
			});
			setStatus("invalid");
		}
	}, [task, taskId]);

	// The stored id covers an import that has already finished; the company-wide lookup covers a new
	// tab, a cleared session, or a different admin arriving mid-import.
	const restoreAttempted = useRef(false);
	useEffect(() => {
		if (restoreAttempted.current || isCompanySettingsPending || isActiveTasksPending) return;
		restoreAttempted.current = true;
		// Both lookups can settle after the admin has already chosen a file, and that takes precedence.
		if (taskId || status !== "idle") return;

		const rawStored = companyId ? sessionStorage.getItem(taskStorageKey(companyId)) : null;
		// A stored id still listed as active gets the same staleness guard as a discovered one. One that
		// has dropped off the list settled, which is the outcome the stored copy exists to recover. This
		// lookup can also have failed, so the streamed task's own age is the backstop below.
		const storedTask = rawStored ? activeTasks?.find((candidate) => candidate._id === rawStored) : undefined;
		const stored = storedTask && !isStillRunnable(storedTask) ? null : rawStored;
		if (rawStored && !stored) rememberTask(null);

		// Falling through rather than returning: the reservation TTL outlasts the staleness cut-off, so a
		// stranded task and a live one can briefly coexist.
		const resumeId = stored ?? activeTasks?.find(isStillRunnable)?._id ?? null;
		if (!resumeId) return;

		rememberTask(resumeId);
		setStatus("importing");
	}, [companyId, isCompanySettingsPending, isActiveTasksPending, activeTasks]);

	// The stream carries the only outcome and does not reconnect after a clean EOF, which is what the
	// load balancer's idle close looks like — without this the page spins on "Importing" forever.
	useEffect(() => {
		if (status !== "importing" || !taskId) return;

		const isTerminal = task?.status === "completed" || task?.status === "failed";
		const streamDied = !isConnected && !isRetrying && (streamError !== null || hasConnected);
		if (isTerminal || !streamDied) return;

		setFile(null);
		// Retained only for a task the stream actually resolved. An id it never resolved has nothing to
		// recover — a live one is found again by the company-wide lookup, a bad one would report forever.
		showFailure(STREAM_LOST_MESSAGE, { retainTaskId: !!task });
	}, [status, taskId, task, streamError, isConnected, isRetrying, hasConnected]);

	const onFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
		const selectedFile = event.target.files?.[0];
		// Allow re-selecting the same file to re-validate from scratch.
		event.target.value = "";
		if (!selectedFile) return;

		setFileName(selectedFile.name);
		if (selectedFile.size > MAX_FIELD_HISTORY_IMPORT_FILE_BYTES) {
			showFileError(FIELD_HISTORY_IMPORT_FILE_TOO_LARGE_MESSAGE);
			return;
		}

		setFile(selectedFile);
		setResult(null);
		rememberTask(null);
		setFailure(null);
		setStatus("validating");
		validateMutation.mutate(selectedFile, {
			onSuccess: (data) => {
				setResult(data);
				setStatus(data.success ? "valid" : "invalid");
			},
			onError: () => reset(),
		});
	};

	const onUpload = () => {
		if (!file) return;
		// A retry has to drop the banner from the attempt that failed, or it outlives its own state.
		setFailure(null);
		setStatus("importing");
		// Upload is only enabled once validation succeeded, so the count it produced is available here.
		importMutation.mutate(
			{ file, employeeCount: result?.employeeCount ?? 0 },
			{
				onSuccess: (data) => rememberTask(data.taskId),
				// Nothing was imported on any of these, so the file stays selected either way.
				onError: (error) => {
					const message = (error as { data?: { message?: string } })?.data?.message;
					showRetryableFailure(message || "The import couldn't be started. Please try again.");
				},
			}
		);
	};

	const entryCount = result?.entryCount ?? 0;
	const employeeCount = result?.employeeCount ?? 0;
	const isImporting = status === "importing";
	// Suppressed under a failure, as is the results slot below — otherwise the chain falls through to the
	// idle empty state and claims no file is chosen. Upload stays armed, so the file is not lost.
	const showSuccessBanner = status === "valid" && result?.success && !failure;
	const uploadDisabled = status !== "valid";

	// The server's re-validated count wins once it has one; until then this file's own count stands in
	// so the panel isn't showing no total while the stream connects.
	const importTotal = task?.progressTotal || employeeCount;

	// The live region is always mounted: one rendered together with its text announces nothing.
	const statusAnnouncement = (() => {
		if (failure) return `${failure.title}. ${failure.message}`;

		switch (status) {
			case "validating":
				return "Checking your file.";
			case "importing":
				return "Importing entries.";
			case "invalid":
				return `Found ${result?.totalErrorCount ?? 0} ${pluralizer(result?.totalErrorCount ?? 0, "error", "errors")}. Nothing was imported.`;
			case "valid":
				return `No errors found. ${entryCount} ${pluralizer(entryCount, "entry", "entries")} across ${employeeCount} ${pluralizer(employeeCount, "employee", "employees")} ready to import. Upload is now enabled.`;
			default:
				return "";
		}
	})();

	return (
		<Sentry.ErrorBoundary fallback={<ErrorPage code={500} />}>
			<Spacer height={4} />
			<AdminContainer>
				<PageTitle title="Import Field History" />
				{/* Caps the three-sentence subtitle for legibility; the heading is too short to be affected. */}
				<div className="max-w-[520px]">
					<AdminTitle description={SUBTITLE}>Import Field History</AdminTitle>
				</div>
				<div className="sr-only" role="status" aria-live="polite">
					{statusAnnouncement}
				</div>
				<div className="flex flex-col gap-6">
					<div className="flex items-center justify-between gap-4">
						<input
							ref={fileInputRef}
							type="file"
							accept=".csv,text/csv"
							className="hidden"
							onChange={onFileSelected}
							aria-label="Choose a CSV file of field history entries"
						/>
						<div className="flex items-center gap-3">
							<ButtonStandard
								dangerouslySetClassName="w-auto"
								onClick={() => fileInputRef.current?.click()}
								disabled={status === "validating" || isImporting}
							>
								Choose a .CSV file
							</ButtonStandard>
							{/* The input is hidden and cleared on every change, so the browser shows no filename. */}
							{file ? (
								<Text size="paragraph-3" weight="regular" color="neutral-7">
									{file.name}
								</Text>
							) : null}
						</div>
						<ButtonStandard
							color="green"
							dangerouslySetClassName="w-auto"
							disabled={uploadDisabled}
							onClick={onUpload}
						>
							Upload
						</ButtonStandard>
					</div>
					<ExpectedFormatPanel />
					{failure ? (
						<PageBanner
							color="red"
							icon={faTriangleExclamation}
							mainTitle={failure.title}
							subTitle={failure.message}
						/>
					) : null}
					{isImporting ? (
						<ImportProgress progressCount={task?.progressCount ?? 0} progressTotal={importTotal} />
					) : showSuccessBanner ? (
						<PageBanner
							color="green"
							icon={faCircleCheck}
							mainTitle="No errors found"
							subTitle={
								<>
									{entryCount} field history {pluralizer(entryCount, "entry", "entries")} across{" "}
									{employeeCount} {pluralizer(employeeCount, "employee", "employees")}{" "}
									{pluralizer(entryCount, "is", "are")} ready to import. Select{" "}
									<Text size="caption" weight="semi-bold" overrideElement="span">
										Upload
									</Text>{" "}
									to add {pluralizer(entryCount, "it", "them")}. Current values will not change.
								</>
							}
						/>
					) : status === "failed" || failure ? null : (
						<ValidationResults
							status={status === "validating" ? "validating" : status === "invalid" ? "invalid" : "idle"}
							fileName={fileName}
							errors={result?.errors ?? []}
							totalErrorCount={result?.totalErrorCount ?? 0}
						/>
					)}
				</div>
			</AdminContainer>
		</Sentry.ErrorBoundary>
	);
};

export default ImportFieldHistory;
